import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import Title from 'src/components/Title';
import { useGhgOrganization } from 'src/common/hooks';
import { getFirstDateOfPeriod, timeConverter } from 'src/utils/timeConverter';
import { IntuitiveCalendarPeriod } from 'src/views/components/IntuitiveCalendar';
import ConfirmationModal from 'src/components/ConfirmationModal';
import {
  EmployeeSurveyCategory,
  useCopyEmployeeSurveyPeriod,
  useEmployeeSurveyResponses,
} from '../hooks';

const ALL_CATEGORIES: EmployeeSurveyCategory[] = [
  'commute',
  'remote',
  'business',
  'hotel',
];

const defaultCategories = (): Record<EmployeeSurveyCategory, boolean> => ({
  commute: true,
  remote: true,
  business: true,
  hotel: true,
});

export default function CopyPeriodForm() {
  const { t } = useTranslation('ghg', {
    keyPrefix: 'employeeSurveys.copyPeriod',
  });
  const organization = useGhgOrganization();
  const reportingPeriod = organization.co2.reporting_freq;
  const queryClient = useQueryClient();
  const copyMutation = useCopyEmployeeSurveyPeriod();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'source' | 'target'>('source');
  const [sourceDate, setSourceDate] = useState(new Date());
  const [targetDate, setTargetDate] = useState(new Date());
  const [categories, setCategories] = useState(defaultCategories());
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [existingCount, setExistingCount] = useState(0);

  const sourcePeriod = timeConverter(sourceDate, reportingPeriod);
  const targetPeriod = timeConverter(targetDate, reportingPeriod);

  const { _data: sourceRows } = useEmployeeSurveyResponses({
    organization_id: organization.id,
    period: sourcePeriod,
  });
  const { _data: targetRows } = useEmployeeSurveyResponses({
    organization_id: organization.id,
    period: targetPeriod,
  });

  const crossYearWarning =
    sourcePeriod.slice(0, 4) !== targetPeriod.slice(0, 4);
  const sourceIsEmpty = sourceRows.length === 0;
  const selectedCategories = ALL_CATEGORIES.filter(
    (category) => categories[category]
  );

  const resetState = () => {
    setStep('source');
    setSourceDate(new Date());
    setTargetDate(new Date());
    setCategories(defaultCategories());
    setMergeConfirmOpen(false);
    setExistingCount(0);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const handleToggleCategory = (category: EmployeeSurveyCategory) => {
    setCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const submitCopy = (allowExistingTarget: boolean) => {
    copyMutation.mutate(
      {
        organization_id: organization.id,
        source_period: sourcePeriod,
        target_period: targetPeriod,
        categories: selectedCategories,
        allow_existing_target: allowExistingTarget,
      },
      {
        onSuccess: (data) => {
          toast.success(
            t('success', {
              copied: data.copied,
              skipped: data.skipped_existing,
            }) as string
          );
          queryClient.invalidateQueries(['employee-survey-responses']);
          handleClose();
        },
        onError: (copyError: any) => {
          const status = copyError?.response?.status;
          const existing = copyError?.response?.data?.existing_count;
          if (status === 409 && typeof existing === 'number') {
            setExistingCount(existing);
            setMergeConfirmOpen(true);
            return;
          }
          if (status === 409) {
            toast.error(t('raceError') as string);
            return;
          }
          toast.error(
            copyError?.response?.data?.detail || (t('raceError') as string)
          );
        },
      }
    );
  };

  const handleSubmit = () => submitCopy(false);

  const handleMergeConfirm = () => {
    setMergeConfirmOpen(false);
    submitCopy(true);
  };

  const handleMergeCancel = () => {
    setMergeConfirmOpen(false);
    setExistingCount(0);
  };

  return (
    <>
      <Button startIcon={<ContentCopyIcon />} onClick={handleOpen}>
        {t('button')}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography sx={{ textAlign: 'center' }} variant="h3">
            {t('title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {step === 'source' && (
            <>
              <Title title={t('sourceStep')} description={null} />
              <IntuitiveCalendarPeriod
                reportingPeriod={reportingPeriod}
                selectedDate={getFirstDateOfPeriod(
                  sourceDate,
                  reportingPeriod ?? 'M'
                )}
                setSelectedDate={setSourceDate}
              />
            </>
          )}
          {step === 'target' && (
            <>
              <Title title={t('targetStep')} description={null} />
              <IntuitiveCalendarPeriod
                reportingPeriod={reportingPeriod}
                selectedDate={getFirstDateOfPeriod(
                  targetDate,
                  reportingPeriod ?? 'M'
                )}
                setSelectedDate={setTargetDate}
              />
              <FormGroup>
                {ALL_CATEGORIES.map((category) => (
                  <FormControlLabel
                    key={category}
                    control={
                      <Checkbox
                        checked={categories[category]}
                        onChange={() => handleToggleCategory(category)}
                      />
                    }
                    label={t(`categories.${category}`)}
                  />
                ))}
              </FormGroup>
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t('counts', {
                  sourceCount: sourceRows.length,
                  targetCount: targetRows.length,
                })}
              </Typography>
              {sourceIsEmpty && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('sourceEmpty')}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('recalcNote')}
              </Typography>
              {crossYearWarning && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('crossYearWarning')}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'source' && (
            <>
              <Button onClick={handleClose}>
                <Trans i18nKey="common.cancel" />
              </Button>
              <Button variant="contained" onClick={() => setStep('target')}>
                {t('targetStep')}
              </Button>
            </>
          )}
          {step === 'target' && (
            <>
              <Button onClick={() => setStep('source')}>
                {t('sourceStep')}
              </Button>
              <Button
                variant="contained"
                disabled={selectedCategories.length === 0 || sourceIsEmpty}
                onClick={handleSubmit}
              >
                {t('button')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={mergeConfirmOpen}
        titleKey="ghg:employeeSurveys.copyPeriod.mergeConfirmation.title"
        onTrue={handleMergeConfirm}
        onFalse={handleMergeCancel}
        onClose={handleMergeCancel}
      >
        <Typography>
          {t('mergeConfirmation.text', { count: existingCount })}
        </Typography>
      </ConfirmationModal>
    </>
  );
}
